/**
 * Device detection utilities for App Store page
 */

export interface DeviceInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  deviceType: "ios" | "android" | "desktop";
}

/**
 * Detect user's device type based on user agent
 */
export const detectDevice = (): DeviceInfo => {
  if (typeof window === "undefined") {
    // Server-side rendering fallback
    return {
      isIOS: false,
      isAndroid: false,
      isMobile: false,
      deviceType: "desktop",
    };
  }

  const userAgent = window.navigator.userAgent.toLowerCase();

  // iOS detection
  const isIOS =
    /iphone|ipad|ipod/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  // Android detection
  const isAndroid = /android/.test(userAgent);

  // Mobile detection
  const isMobile = isIOS || isAndroid || /mobile/.test(userAgent);

  // Determine device type
  let deviceType: "ios" | "android" | "desktop" = "desktop";
  if (isIOS) {
    deviceType = "ios";
  } else if (isAndroid) {
    deviceType = "android";
  }

  return {
    isIOS,
    isAndroid,
    isMobile,
    deviceType,
  };
};

/**
 * Get appropriate app store URL based on device
 */
export const getAppStoreUrl = (
  deviceType?: "ios" | "android" | "desktop",
): string => {
  const device = deviceType || detectDevice().deviceType;

  const urls = {
    ios: "https://apps.apple.com/vn/app/locuno/id6753800628",
    android: "https://play.google.com/store/apps/details?id=com.locunoapp",
    desktop: "https://apps.apple.com/vn/app/locuno/id6753800628", // Default to iOS
  };

  return urls[device];
};

/**
 * Get appropriate button text based on device
 */
export const getDownloadButtonText = (
  deviceType?: "ios" | "android" | "desktop",
): { full: string; short: string } => {
  const device = deviceType || detectDevice().deviceType;

  const texts = {
    ios: {
      full: "Tải từ App Store",
      short: "Tải Xuống",
    },
    android: {
      full: "Tải từ Google Play",
      short: "Tải Xuống",
    },
    desktop: {
      full: "Tải Xuống",
      short: "Tải Xuống",
    },
  };

  return texts[device];
};

/**
 * Extended Window interface for Google Analytics
 */
interface WindowWithAnalytics extends Window {
  gtag?: (
    command: string,
    eventName: string,
    eventParams: Record<string, string | boolean>,
  ) => void;
  ga?: (
    command: string,
    hitType: string,
    eventCategory: string,
    eventAction: string,
    eventLabel: string,
  ) => void;
}

/**
 * Track download with device info
 */
export const trackDownloadWithDevice = (
  deviceType: "ios" | "android" | "desktop",
) => {
  const win = window as WindowWithAnalytics;

  // Google Analytics 4 event tracking
  if (typeof window !== "undefined" && win.gtag) {
    win.gtag("event", "app_download", {
      event_category: "engagement",
      event_label: "app_store_page",
      app_name: "Locuno Community Hub",
      download_source: "app_store_landing_page",
      store_type:
        deviceType === "ios" ? "apple_app_store" : "google_play_store",
      device_type: deviceType,
      auto_detected: true,
    });
  }

  // Alternative: Google Analytics Universal Analytics format
  if (typeof window !== "undefined" && win.ga) {
    const storeName =
      deviceType === "ios" ? "Apple App Store" : "Google Play Store";
    win.ga(
      "send",
      "event",
      "App Download",
      "Click",
      `${storeName} - Auto Detected ${deviceType}`,
    );
  }
};
